import type { TypeTableColumn } from '@thefarmersfront/kitchen';

import { BadgeCell, TextButtonCell } from '~/components/common';

import { formatNumber } from '~/utils/common';
import { formatDate } from '~/utils/date';
import { formatReceiveExpectedDateTime } from '~/utils/receive';

import { DATE_FORMAT } from '~/constants/common';

import { RECEIVE_REPORT_STATUS_COLOR_MAP } from './receiveBadgeColorMaps';

import type { TypeReceiveReportHistory } from '~/types/receive.types';

const dateTimeColumn = (
  accessorKey: 'receivedDateTime' | 'createdAt' | 'statusUpdatedAt',
  header: string
): TypeTableColumn<TypeReceiveReportHistory> => ({
  accessorKey,
  header,
  size: 160,
  cell: ({ getValue }) => formatDate(getValue<string>(), DATE_FORMAT.DATE_TIME) ?? '-',
});

const countColumn = (accessorKey: 'sku' | 'unit', header: string): TypeTableColumn<TypeReceiveReportHistory> => ({
  accessorKey,
  header,
  size: 100,
  cell: ({ getValue }) => {
    const value = getValue<number | null>();
    return value ? formatNumber(value) : '-';
  },
  meta: { cellStyle: { textAlign: 'right' } },
});

/**
 * [입고관리 > 입고 리포트 관리] 페이지 테이블
 */
const getReceiveReportListColumnDefs = ({
  onClickViewDetail,
}: {
  onClickViewDetail: (data: TypeReceiveReportHistory) => void;
}) => {
  const columnDefs: TypeTableColumn<TypeReceiveReportHistory>[] = [
    {
      id: 'no',
      header: 'No',
      cell: ({ row }) => row.index + 1,
      size: 72,
    },
    {
      accessorKey: 'reportCode',
      header: '리포트 ID',
      size: 180,
    },
    {
      accessorKey: 'requestCode',
      header: '입고요청번호',
      size: 180,
    },
    {
      accessorKey: 'reportStatus',
      header: '리포트 상태',
      size: 127,
      cell: ({ getValue }) => {
        const value = getValue<TypeReceiveReportHistory['reportStatus']>();
        return value ? (
          <BadgeCell value={value} colorMap={RECEIVE_REPORT_STATUS_COLOR_MAP} variant="light" status />
        ) : (
          '-'
        );
      },
    },
    {
      accessorKey: 'releaseName',
      header: '입고 방법',
      size: 85,
    },
    countColumn('sku', '리포트 SKU'),
    countColumn('unit', '리포트 Unit'),
    {
      accessorKey: 'taskNames',
      header: '작업 유형',
      size: 200,
      cell: ({ getValue }) => {
        const value = getValue<TypeReceiveReportHistory['taskNames']>();
        return value ? value.join(', ') : '-';
      },
    },
    {
      id: 'viewDetail',
      header: '',
      size: 80,
      cell: ({ row }) => (
        <TextButtonCell showDashIfNoValue={false} onClick={() => onClickViewDetail(row.original)}>
          상세보기
        </TextButtonCell>
      ),
    },
    {
      id: 'receivingExpectedDate',
      accessorFn: (data) => formatReceiveExpectedDateTime(data.receivingExpectedDate, data.receivingExpectedTime),
      header: '입고예정일시',
      size: 200,
    },
    dateTimeColumn('receivedDateTime', '입고확정일시'),
    dateTimeColumn('createdAt', '리포트 발행일시'),
    dateTimeColumn('statusUpdatedAt', '리포트 상태변경일시'),
  ];

  return { columnDefs };
};

export default getReceiveReportListColumnDefs;
